import type { Id } from '../core/model';

/** Report again only when the needed scale moved by more than this fraction. */
const CHANGE_THRESHOLD = 0.1;

/**
 * Collects, per pass, the largest on-canvas scale each source is drawn at and
 * reports it when it moved enough to matter, so sources that rasterise their
 * own picture (text, shapes) can match the scale they are shown at.
 */
export class SourceScaleTracker {
  private readonly pass = new Map<Id, number>();
  private readonly reported = new Map<Id, number>();

  note(sourceId: Id, scale: number): void {
    const value = Math.abs(scale);
    if (!Number.isFinite(value) || value <= 0) return;
    if (value > (this.pass.get(sourceId) ?? 0)) this.pass.set(sourceId, value);
  }

  /** Emits the sources whose scale changed since the last report, then starts a new pass. */
  flush(emit: (sourceId: Id, scale: number) => void): void {
    for (const [id, value] of this.pass) {
      const last = this.reported.get(id);
      if (last !== undefined && Math.abs(value - last) / last <= CHANGE_THRESHOLD) continue;
      this.reported.set(id, value);
      emit(id, value);
    }
    this.pass.clear();
  }

  forget(sourceId: Id): void {
    this.pass.delete(sourceId);
    this.reported.delete(sourceId);
  }
}
